import React, { useEffect, useState } from 'react';
import { StatusBadge } from './StatusBadge';
import { PilotFeedback } from './PilotFeedback';

export function OperationsRadarBriefPanel({ onAsk, onFeedback, busy, radarAvailable, radarVersion }) {
  const [question, setQuestion] = useState('What needs attention today?');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setResult(null);
    setError(null);
  }, [radarVersion]);

  async function submit(nextQuestion = question) {
    const value = String(nextQuestion ?? '').trim();
    if (!value || busy || !radarAvailable) return;

    setQuestion(value);
    setError(null);

    try {
      setResult(await onAsk(value));
    } catch (err) {
      setResult(null);
      setError(
        err?.correlationId
          ? `${err.message} Correlation: ${err.correlationId}`
          : err.message
      );
    }
  }

  return (
    <aside className="copilot-panel operations-brief-panel">
      <div className="copilot-panel__top">
        <div>
          <div className="eyebrow">Executive Operations Copilot</div>
          <h2>What needs attention?</h2>
        </div>
        <StatusBadge tone="success">Grounded</StatusBadge>
      </div>

      <div className="copilot-routing-note">
        Cross-order totals and blocker categories are calculated deterministically before AI summarizes them.
      </div>

      <div className="suggestion-list">
        {[
          'What needs attention today?',
          'Where are overdue orders concentrated?',
          'Which current blocker affects the most overdue orders?',
          'Which oldest orders should operations investigate first?'
        ].map((item) => (
          <button key={item} disabled={!radarAvailable || busy} onClick={() => submit(item)}>
            {item}
          </button>
        ))}
      </div>

      <div className="ask-box">
        <textarea
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Ask about the current exception radar…"
          disabled={!radarAvailable || busy}
          maxLength={1200}
          onKeyDown={(event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
        />
        <div className="ask-box__footer">
          <span>Read-only management intelligence</span>
          <button
            className="primary-button"
            disabled={!radarAvailable || !question.trim() || busy}
            onClick={() => submit()}
          >
            {busy ? 'Working…' : 'Ask Copilot'}
          </button>
        </div>
      </div>

      {error ? <div className="inline-error">{error}</div> : null}

      {result?.brief ? (
        <>
          <div className="copilot-answer radar-brief-answer">
            <div className="order-brief-heading">
              <span className="copilot-answer__situation-label">Operations brief</span>
              <strong>{result.brief.headline}</strong>
            </div>

            <BriefList title="Key findings" values={result.brief.keyFindings} />
            <BriefList title="Management focus" values={result.brief.managementFocus} accent />
            <BriefList title="Unknowns / limits" values={result.brief.unknowns} />

            <div className="answer-footer">
              <span>Confidence: <strong>{result.brief.confidence || '—'}</strong></span>
              <span>Cross-order radar</span>
            </div>
          </div>
          <PilotFeedback response={result} onSubmit={onFeedback} />
        </>
      ) : (
        <div className="copilot-empty">
          <strong>Start with the exception picture.</strong>
          <p>Use the brief to identify where overdue work is concentrated, then drill into individual orders for Case Brief and Delay Analysis.</p>
        </div>
      )}
    </aside>
  );
}

function BriefList({ title, values = [], accent = false }) {
  if (!values.length) return null;
  return (
    <div className={`answer-section ${accent ? 'radar-focus-section' : ''}`}>
      <h3>{title}</h3>
      <ul>
        {values.map((value, index) => <li key={`${value}-${index}`}>{value}</li>)}
      </ul>
    </div>
  );
}
